// ============================================================================
// FRANCE TAX CALCULATION - Impôt sur le revenu 2024
// Source: Direction Générale des Finances Publiques
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";
import { calculateTaxByBrackets } from "./utils";

export const FRANCE_TAX_CONFIG: CountryTaxData = {
  socialSecurityRate: 22, // Approximate total employee contributions
  taxBrackets: [
    { min: 0, max: 11294, rate: 0 },
    { min: 11294, max: 28797, rate: 11 },
    { min: 28797, max: 82341, rate: 30 },
    { min: 82341, max: 177106, rate: 41 },
    { min: 177106, max: Infinity, rate: 45 },
  ],
  personalAllowance: 0, // Uses quotient familial system
  marriedAllowance: 0,
  childAllowances: [0, 0, 0],
  currency: "EUR",
};

// Social Security contribution rates (employee share)
const SS_RATES = {
  // CSG/CRDS on gross salary
  csgDeductible: 6.8, // CSG déductible
  csgNonDeductible: 2.4, // CSG non déductible + CRDS
  // Other contributions (approximate)
  retirement: 6.9, // Retraite complémentaire
  unemployment: 0, // À charge de l'employeur depuis 2019
  health: 0, // À charge de l'employeur
  other: 0.5, // Other minor contributions
};

// Quotient familial parts
const PARTS = {
  single: 1,
  married: 2,
  childPart: 0.5, // First two children
  thirdChildPart: 1, // Third child onwards
};

export interface FranceTaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
}

/**
 * Calcula el Impôt sur le revenu y cotisations sociales de Francia
 * 
 * Características del sistema francés:
 * - Quotient familial (división por partes según situación familiar)
 * - CSG/CRDS sobre salario bruto
 * - Impuesto calculado por foyer fiscal
 */
export function calculateFranceTax({
  grossIncome,
  familyStatus,
  dependents,
}: FranceTaxParams): TaxCalculationResult {
  const config = FRANCE_TAX_CONFIG;

  // ============================================================================
  // SOCIAL SECURITY (Cotisations sociales)
  // ============================================================================
  
  // CSG/CRDS base is 98.25% of gross salary
  const csgBase = grossIncome * 0.9825;
  const csgTotal = (csgBase * (SS_RATES.csgDeductible + SS_RATES.csgNonDeductible)) / 100;
  
  // Other contributions
  const otherContribs = (grossIncome * (SS_RATES.retirement + SS_RATES.other)) / 100;
  
  const socialSecurity = csgTotal + otherContribs;

  // ============================================================================
  // INCOME TAX (Impôt sur le revenu)
  // ============================================================================

  // Deductible CSG reduces taxable income
  const deductibleCSG = (csgBase * SS_RATES.csgDeductible) / 100;
  
  // 10% professional expenses deduction (minimum 495€, maximum 14,171€)
  const fraisPro = Math.min(Math.max(grossIncome * 0.1, 495), 14171);
  
  // Net imposable
  const netImposable = grossIncome - deductibleCSG - fraisPro;

  // Quotient familial - calculate number of parts
  let parts = familyStatus === "married" ? PARTS.married : PARTS.single;
  
  // Add parts for children
  if (dependents >= 1) parts += PARTS.childPart; // 1st child
  if (dependents >= 2) parts += PARTS.childPart; // 2nd child
  for (let i = 2; i < dependents; i++) {
    parts += PARTS.thirdChildPart; // 3rd child onwards = 1 part each
  }

  // Single parent bonus
  if (familyStatus === "single" && dependents > 0) {
    parts += 0.5; // Half part bonus for single parent
  }

  // Calculate tax with quotient familial
  const quotientFamilial = netImposable / parts;
  const taxPerPart = calculateTaxByBrackets(quotientFamilial, config.taxBrackets);
  
  // Total tax (limited by plafonnement du quotient familial)
  // Maximum benefit per half-part: 1,759€ (2024)
  const maxBenefitPerHalfPart = 1759;
  const referenceParts = familyStatus === "married" ? 2 : 1;
  const extraHalfParts = (parts - referenceParts) * 2; // Convert to half-parts
  
  // Tax without family quotient benefit
  const taxWithoutQF = calculateTaxByBrackets(netImposable / referenceParts, config.taxBrackets) * referenceParts;
  
  // Tax with family quotient
  let taxWithQF = taxPerPart * parts;
  
  // Apply ceiling if benefit is too high
  const benefit = taxWithoutQF - taxWithQF;
  const maxBenefit = extraHalfParts * maxBenefitPerHalfPart;
  
  let incomeTax: number;
  if (benefit > maxBenefit) {
    incomeTax = taxWithoutQF - maxBenefit;
  } else {
    incomeTax = taxWithQF;
  }

  const taxRate = grossIncome > 0 ? (incomeTax / grossIncome) * 100 : 0;

  // Build breakdown with French labels
  const breakdown: DeductionBreakdown[] = [];

  if (incomeTax > 0) {
    breakdown.push({
      id: "impot_revenu",
      amount: Math.max(0, incomeTax),
      rate: taxRate,
      type: "tax",
    });
  }

  if (csgTotal > 0) {
    breakdown.push({
      id: "csg_crds",
      amount: csgTotal,
      rate: grossIncome > 0 ? (csgTotal / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  if (otherContribs > 0) {
    breakdown.push({
      id: "cotisations",
      amount: otherContribs,
      rate: grossIncome > 0 ? (otherContribs / grossIncome) * 100 : 0,
      type: "social",
    });
  }

  return {
    tax: Math.max(0, incomeTax),
    taxRate,
    socialSecurity,
    allowances: fraisPro + deductibleCSG,
    breakdown,
  };
}
